import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select"
import type { DashboardDatePreset } from "../lib/presets"
import type { DashboardFilterState } from "../state/dashboardReducer"
import { DateRangePicker } from "@/components/DateRangePicker"
import { DashboardFilterDropdowns } from "./DashboardFilterDropdowns"
import { DashboardFiltersMobileDialog } from "./DashboardFiltersMobileDialog"
import { countDashboardActiveFilters } from "../lib/statusFilters"

/**
 * Filter bar ported from `dashboard.html:1111-1168`.
 */

interface BuyerOption {
  id: number
  label: string
}

interface DashboardFiltersProps {
  state: DashboardFilterState
  onNetworksChange: (values: string[]) => void
  onBuyerStatusesChange: (values: string[]) => void
  onAccountStatusesChange: (values: string[]) => void
  onBuyersChange: (ids: number[]) => void
  onSearchChange: (value: string) => void
  onRefresh: () => void
  onPresetChange: (preset: DashboardDatePreset) => void
  onCustomRangeApply: (
    startIso: string,
    endIso: string,
    displayLabel: string,
  ) => void
  buyerOptions?: BuyerOption[]
}

const PRESETS: Array<{ key: DashboardDatePreset; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "thisweek", label: "This week" },
]

export function DashboardFilters({
  state,
  onNetworksChange,
  onBuyerStatusesChange,
  onAccountStatusesChange,
  onBuyersChange,
  onSearchChange,
  onRefresh,
  onPresetChange,
  onCustomRangeApply,
  buyerOptions = [],
}: DashboardFiltersProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [refreshAnimating, setRefreshAnimating] = useState(false)
  const refreshTimerRef = useRef<number | null>(null)
  const customRangeWrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current)
      }
    }
  }, [])

  const placeholder =
    state.activeTab === "buyer" ? "Search buyers..." : "Search accounts..."

  const activeFilterCount = countDashboardActiveFilters(
    state.selectedNetworks,
    state.activeTab === "account"
      ? state.selectedAccountStatuses
      : state.selectedBuyerStatuses,
    state.selectedBuyers,
    state.activeTab,
  )

  const handleRefresh = () => {
    onRefresh()
    setRefreshAnimating(true)
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current)
    }
    refreshTimerRef.current = window.setTimeout(() => {
      setRefreshAnimating(false)
      refreshTimerRef.current = null
    }, 650)
  }

  const refreshButton = (
    <Button
      type="button"
      onClick={handleRefresh}
      title="Refresh data"
      className="h-9 w-9 shrink-0 rounded-md border border-[#676d78] bg-[#4f5561] p-0 text-base shadow-sm transition-colors hover:bg-[#5a616f] md:h-8 md:w-8"
      aria-label="Refresh data"
    >
      <span aria-hidden className={refreshAnimating ? "animate-spin" : ""}>
        &#128260;
      </span>
    </Button>
  )

  return (
    <div className="mb-1.5 rounded-md border border-[#3f3f3f] bg-[#252525] px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <div className="flex w-full min-w-0 flex-col gap-1.5 md:flex-row md:items-center md:gap-2">
        <div className="hidden min-w-0 flex-1 items-center gap-1.5 overflow-x-auto overscroll-x-contain py-0.5 [-webkit-overflow-scrolling:touch] md:flex">
          <DashboardFilterDropdowns
            activeTab={state.activeTab}
            selectedNetworks={state.selectedNetworks}
            onNetworksChange={onNetworksChange}
            selectedBuyerStatuses={state.selectedBuyerStatuses}
            onBuyerStatusesChange={onBuyerStatusesChange}
            selectedAccountStatuses={state.selectedAccountStatuses}
            onAccountStatusesChange={onAccountStatusesChange}
            selectedBuyers={state.selectedBuyers}
            onBuyersChange={onBuyersChange}
            buyerOptions={buyerOptions}
          />
        </div>

        <div className="flex min-w-0 items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="relative flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-[#555] bg-[#2d2d2d] px-3 text-xs font-medium text-[#e0e0e0] shadow-sm hover:bg-[#3a3a3a]"
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#7488e8] px-1 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          {refreshButton}
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-1.5 md:ml-auto md:flex-none md:justify-end">
          <div className="hidden md:block">{refreshButton}</div>

          <div
            ref={customRangeWrapRef}
            className="relative flex w-full min-w-0 flex-nowrap items-center gap-1 md:w-auto"
          >
            <div className="min-w-0 flex-1 md:hidden">
              <Select
                value={state.datePreset}
                onValueChange={(value) => {
                  if (value === "custom") {
                    setPickerOpen(true)
                    return
                  }
                  onPresetChange(value as DashboardDatePreset)
                }}
              >
                <SelectTrigger className="h-9 w-full border border-[#555] bg-[#2d2d2d] text-xs text-[#e0e0e0] focus:ring-0">
                  <span className="truncate">{state.dateDisplayLabel}</span>
                </SelectTrigger>
                <SelectContent className="border-[#555] bg-[#2d2d2d] text-white">
                  {PRESETS.map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">
                    {state.datePreset === "custom"
                      ? state.dateDisplayLabel
                      : "Custom range…"}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="hidden shrink-0 flex-nowrap items-center gap-1 md:inline-flex">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onPresetChange(p.key)}
                  className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium shadow-sm transition-colors sm:px-3 sm:text-sm ${
                    state.datePreset === p.key
                      ? "border-[#8fa0ef]/35 bg-[#7488e8] text-white"
                      : "border-[#555] bg-[#2d2d2d] text-[#e0e0e0] hover:bg-[#3a3a3a]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                className="flex h-8 max-w-none shrink-0 items-center gap-2 truncate rounded-md border border-[#555] bg-[#2d2d2d] px-2.5 text-xs font-medium text-[#e0e0e0] shadow-sm transition-colors hover:bg-[#3a3a3a] sm:px-3 sm:text-sm"
                aria-expanded={pickerOpen}
                aria-haspopup="dialog"
                aria-label="Custom date range"
              >
                <span className="truncate">{state.dateDisplayLabel}</span>
                <span className="shrink-0 text-[0.7rem]" aria-hidden>
                  &#9660;
                </span>
              </button>
            </div>

            <DateRangePicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              initialStart={state.startDate || null}
              initialEnd={state.endDate || null}
              outsideBoundaryRef={customRangeWrapRef}
              onApply={(s, e, label) => {
                onCustomRangeApply(s, e, label)
                setPickerOpen(false)
              }}
            />
          </div>
        </div>
      </div>

      <DashboardFiltersMobileDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        activeTab={state.activeTab}
        selectedNetworks={state.selectedNetworks}
        onNetworksChange={onNetworksChange}
        selectedBuyerStatuses={state.selectedBuyerStatuses}
        onBuyerStatusesChange={onBuyerStatusesChange}
        selectedAccountStatuses={state.selectedAccountStatuses}
        onAccountStatusesChange={onAccountStatusesChange}
        selectedBuyers={state.selectedBuyers}
        onBuyersChange={onBuyersChange}
        buyerOptions={buyerOptions}
      />

      <div className="mt-1.5 border-t border-[#3a3a3a] pt-1.5">
        <Input
          type="text"
          value={state.search}
          onChange={(ev) => onSearchChange(ev.target.value)}
          placeholder={placeholder}
          className="h-8 w-full border-[#555] bg-[#2d2d2d] text-sm text-[#e0e0e0] placeholder:text-[#888] focus-visible:ring-1 focus-visible:ring-[#7488e8]"
        />
      </div>
    </div>
  )
}
